import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { logExtraProfileRequest } from '../lib/extraProfileRequest';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import { Plus, LogOut, MessageCircle, X, Lock } from 'lucide-react';
import ChildForm from '../components/ChildForm';
import BrandMark from '../components/BrandMark';
import { FeaturedRewardCard } from '../components/FeaturedRewardCard';
import ChildPinModal from '../components/ChildPinModal';

const AVATARS = {
  deer: 'https://images.unsplash.com/photo-1666158635711-77029b8a6a2d?w=200',
  bear: 'https://images.unsplash.com/photo-1666158589193-f73d7c0f289c?w=200',
};

export default function Profiles() {
  const nav = useNavigate();
  const { user, pickChild, logout, uiLang, billing, isChildUnlocked, markChildUnlocked } = useApp();
  const t = useT(uiLang);
  const [children, setChildren] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showLimit, setShowLimit] = useState(false);
  // Iter-51.35 — profile the user tapped that still needs a PIN check.
  // `null` when no gate is open.
  const [pinGateChild, setPinGateChild] = useState(null);

  const load = async () => {
    const { data } = await api.get('/children');
    setChildren(data);
    if (data.length === 0) setShowForm(true);
  };

  useEffect(() => { load(); }, []);

  const choose = (c) => {
    // Iter-51.35 — PIN gate. Profiles with `has_pin=true` need a per-
    // session unlock before we set them as active. Once unlocked, the
    // token is remembered in AppContext (sessionStorage-backed) so
    // subsequent switches during this session skip the gate.
    if (c.has_pin && !isChildUnlocked(c.child_id)) {
      setPinGateChild(c);
      return;
    }
    pickChild(c);
    nav('/home');
  };

  const onPinUnlocked = (token) => {
    if (!pinGateChild) return;
    markChildUnlocked(pinGateChild.child_id, token);
    pickChild(pinGateChild);
    setPinGateChild(null);
    nav('/home');
  };

  // Family-plan limit is enforced server-side (POST /children 402s);
  // this front-end guard skips the extra request round-trip and pops
  // the upgrade sheet directly.
  //
  // Iter-51.66 — FIX: default the limit to 1 (matches the server's
  // FREE_PROFILE_LIMIT default) so we NEVER silently allow a 2nd
  // profile through if `/billing/status` hasn't resolved yet or
  // fails. The prior `?? 2` fallback was the exact reason the
  // 2nd-profile gate never fired in practice.
  const limit = billing?.free_profile_limit ?? 1;
  const atLimit = children.length >= limit;
  const onAddClick = () => (atLimit ? setShowLimit(true) : setShowForm(true));

  return (
    <div className="min-h-screen p-8">
      <div data-testid="app-header" className="w-full flex justify-center pt-1 pb-6">
        <BrandMark size="md"/>
      </div>
      <div className="flex justify-between items-center mb-12">
        <div>
          <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Hi {user?.name?.split(' ')[0]}</p>
          <h1 className="text-4xl font-extrabold mt-1">{t('chooseProfile')}</h1>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="pushable bg-white text-slate-600 font-bold px-5 py-3 rounded-full border-2 border-slate-200 inline-flex items-center gap-2">
          <LogOut size={18} strokeWidth={3}/> {t('logout')}
        </button>
      </div>

      <div className="flex gap-8 flex-wrap items-start justify-center">
        {children.map((c) => {
          const locked = c.has_pin && !isChildUnlocked(c.child_id);
          return (
            <button data-testid={`child-avatar-${c.child_id}`} key={c.child_id} onClick={() => choose(c)} className="group flex flex-col items-center gap-3">
              {/* Iter-51.40 — wrap the avatar in a `relative` shell so
                  the padlock badge can sit OUTSIDE the circle's
                  `overflow-hidden` clip. Previously the badge lived
                  inside the clipped ring and got sliced by the
                  border-radius, leaving only a thin sliver visible.
                  Now the ring keeps its overflow-hidden (so the
                  photo stays perfectly round) and the badge floats
                  as a sibling, fully visible in the bottom-right. */}
              <div className="relative">
                <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-[#D97398] group-hover:scale-105 transition-transform">
                  {c.avatar_url ? (
                    <img src={c.avatar_url} alt={c.name} className="w-full h-full object-cover"/>
                  ) : (
                    <img src={AVATARS[c.avatar] || AVATARS.deer} alt={c.name} className="w-full h-full object-cover"/>
                  )}
                </div>
                {/* Iter-51.35 — padlock badge for PIN-locked profiles.
                    Iter-51.40 — sized up to 44×44 with a 22px icon so
                    parents recognise it at a glance; solid slate-800
                    when locked (higher contrast on any avatar) and
                    forest green after this-session unlock. */}
                {c.has_pin && (
                  <div data-testid={`child-lock-badge-${c.child_id}`}
                    className={`absolute bottom-0 right-0 w-11 h-11 rounded-full border-[3px] border-white flex items-center justify-center shadow-lg ${
                      locked ? 'bg-slate-800' : 'bg-[#4A9B7F]'
                    }`}
                    aria-label={locked ? 'PIN required' : 'Unlocked this session'}
                    title={locked ? 'PIN required' : 'Unlocked this session'}
                  >
                    <Lock size={22} strokeWidth={3} className="text-white"/>
                  </div>
                )}
              </div>
              <span className="font-extrabold text-xl">{c.name}</span>
              <FeaturedRewardCard reward={c.featured_reward} totalPoints={c.total_points} size="mini"/>
            </button>
          );
        })}
        <button data-testid="add-child-btn" onClick={onAddClick} className="flex flex-col items-center gap-3 group">
          <div className="w-32 h-32 rounded-full bg-[#F4B740] border-4 border-dashed border-[#FF6B1A] flex items-center justify-center group-hover:scale-105 transition-transform">
            <Plus size={48} strokeWidth={3} className="text-[#FF6B1A]"/>
          </div>
          <span className="font-extrabold text-xl text-[#FF6B1A]">{t('addChild')}</span>
        </button>
      </div>

      {showForm && (
        <ChildForm
          showWelcome={children.length === 0}
          onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await load(); }}
          onLimitReached={() => {
            // Iter-51.66 — server said 402 profile_limit_reached; close
            // the form and pop the upgrade sheet so the parent knows
            // what to do. Covers the race where billing hadn't loaded
            // by the time the parent clicked "Add a profile".
            setShowForm(false);
            setShowLimit(true);
          }}
        />
      )}

      {pinGateChild && (
        <ChildPinModal
          child={pinGateChild}
          onUnlocked={onPinUnlocked}
          onCancel={() => setPinGateChild(null)}
        />
      )}

      {showLimit && (
        <ProfileLimitModal
          user={user}
          limit={limit}
          waUrl={billing?.whatsapp_url_add_child}
          onClose={() => setShowLimit(false)}
          onLogout={async () => { await logout(); nav('/', { replace: true }); }}
        />
      )}
    </div>
  );
}


function ProfileLimitModal({ user, limit, waUrl, onClose, onLogout }) {
  const fallback = `https://wa.me/919599668449?text=${encodeURIComponent(`Hi, I would like to add more profiles on ReadBuddy. My name is ${user?.name || ''} and my email is ${user?.email || ''}`)}`;
  return (
    <div data-testid="profile-limit-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <button data-testid="profile-limit-close" onClick={onClose} className="absolute right-3 top-3 text-slate-400 hover:text-slate-700 p-2">
          <X size={20}/>
        </button>
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-[#FFEDD5] mx-auto mb-3 flex items-center justify-center">
            <Plus size={28} strokeWidth={2.5} className="text-[#FF6B1A]"/>
          </div>
          <h2 className="text-xl font-extrabold text-[#2B2140]">You've reached your free profile limit</h2>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            Your plan includes <span className="font-bold">{limit} profile{limit === 1 ? '' : 's'}</span>. Add a
            second profile with the <span className="font-bold text-[#FF6B1A]">₹299 sibling add-on</span>
            {' '}(same plan limits, private stats). Message us on WhatsApp to activate it.
          </p>
          <a data-testid="profile-limit-whatsapp-btn" href={waUrl || fallback} target="_blank" rel="noopener noreferrer"
            onClick={() => logExtraProfileRequest('profile_limit_modal')}
            className="pushable mt-5 w-full bg-[#25D366] hover:bg-[#20BF5B] text-white font-bold py-3 rounded-full inline-flex items-center justify-center gap-2">
            <MessageCircle size={18} strokeWidth={2.5}/>
            Contact us on WhatsApp
          </a>
          <button data-testid="profile-limit-logout-btn" onClick={onLogout}
            className="mt-3 w-full bg-white border-2 border-slate-200 text-slate-700 font-bold py-3 rounded-full inline-flex items-center justify-center gap-2 text-sm hover:border-slate-400">
            <LogOut size={16} strokeWidth={2.5}/> Log out
          </button>
        </div>
      </div>
    </div>
  );
}
